/**
 * Validation for `return_to` / `next` redirect targets used across the
 * cross-app auth round trips (website → accounts → website/Bridge).
 *
 * Open-redirect rules:
 * - Same-app relative paths ("/foo") are allowed, but protocol-relative
 *   ("//evil.com") and backslash variants ("/\evil.com") are rejected —
 *   browsers resolve both to an external host.
 * - Absolute URLs must have an origin exactly equal to one of the ecosystem
 *   app origins. Exact origin comparison (not prefix matching) prevents
 *   lookalike hosts such as "https://pex.example.net.evil.com".
 *
 * NEXT_PUBLIC_* env vars are read via static member access so Next.js can
 * inline them into client bundles.
 */

function getTrustedOrigins(): string[] {
  const urls = [
    process.env.NEXT_PUBLIC_WEBSITE_URL,
    process.env.NEXT_PUBLIC_ACCOUNTS_URL,
    process.env.NEXT_PUBLIC_BRIDGE_URL,
    process.env.NEXT_PUBLIC_PULSE_URL,
    process.env.NEXT_PUBLIC_CIRCLE_URL,
    process.env.NEXT_PUBLIC_BILLING_URL,
  ];

  const origins: string[] = [];
  for (const url of urls) {
    if (!url) {
      continue;
    }
    try {
      origins.push(new URL(url).origin);
    } catch {
      // Malformed env value — skip rather than trust
    }
  }
  return origins;
}

/**
 * Returns the URL unchanged when it is a safe redirect target, otherwise null.
 * Callers decide the fallback (typically the website homepage).
 */
export function resolveTrustedReturnTo(
  url: string | null | undefined
): string | null {
  if (!url) {
    return null;
  }

  if (url.startsWith("/")) {
    const second = url.charAt(1);
    return second === "/" || second === "\\" ? null : url;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  return getTrustedOrigins().includes(parsed.origin) ? url : null;
}
